import React, { useEffect, useRef, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Dialog, Transition } from '@headlessui/react';
import {
  Button,
  Icon,
  SelectField,
  RadioField,
} from '@unite-us/app-components';
import { wrk31LeadScnProviders } from 'src/common/utils/FeatureFlags/flags';
import { useFind } from 'src/api/APIHooks';
import { Spinner } from 'common/spinners';
import { debounce, get } from 'lodash';
import { useTaskSendOrganizations, useBatchAssignTasks } from './tasksQueries';
import { notifyTaskReassignedSuccess, notifyTaskReassignedError } from './taskNotifications';

const ASSIGN_TO_USER = 'assign_to_user';
const ASSIGN_TO_ORG = 'assign_to_org';

// if you are a lead SCN entity in a Customer Initiative, you can send tasks to child SCN entities
// else you can only send it back to the lead SCN entity (which is the submitting org of the task)

const ReassignTasksModalContent = ({
  show,
  onClose,
  onAfterSubmit,
  selectedTasks,
  currentProviderId,
  leadSCNFlag,
  initialAssigneeType,
}) => {
  const selectedCount = selectedTasks.length;
  const [assigneeType, setAssigneeType] = useState(initialAssigneeType ?? ASSIGN_TO_USER);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('');
  const [debouncedOrgSearchQuery, setDebouncedOrgSearchQuery] = useState('');
  const debouncedSetOrgSearchQuery = useRef(
    debounce((value) => {
      setDebouncedOrgSearchQuery(value);
    }, 200),
  ).current;

  const { data: employeesResponse, isLoading: loadingEmployees } = useFind(
    'employee',
    { provider: currentProviderId },
    { queryConfig: { enabled: !!currentProviderId, placeholderData: undefined } },
  );
  const employeeOptions = get(employeesResponse, 'data.data', []).map((e) => ({
    value: e.id,
    label: `${e.first_name ?? ''} ${e.last_name ?? ''}`.trim(),
  }));

  const {
    orgOptions,
    isLoading: loadingOrgs,
    orgRadioDisabled,
  } = useTaskSendOrganizations(
    currentProviderId,
    leadSCNFlag,
    debouncedOrgSearchQuery,
  );

  useEffect(() => () => debouncedSetOrgSearchQuery.cancel(), [debouncedSetOrgSearchQuery]);
  const hasUserCreatedTaskSelected = selectedTasks.some((task) => task?.subject_type === 'Employee');
  const isAssignToOrgDisabled = orgRadioDisabled || hasUserCreatedTaskSelected;
  const orgDisabledMessage = hasUserCreatedTaskSelected ?
    [
      'Organization reassignment is unavailable because your selection includes',
      'one or more user-created tasks.',
    ].join(' ') :
    [
      'Organization reassignment is unavailable because there are no eligible',
      'organizations in your network.',
    ].join(' ');

  const assignToOptions = [
    { value: ASSIGN_TO_USER, label: 'User at Your Organization' },
    { value: ASSIGN_TO_ORG, label: 'Organization in Your Network', disabled: isAssignToOrgDisabled },
  ];
  const { mutateAsync: batchAssignTasks, isLoading: isAssigning } = useBatchAssignTasks();

  const handleClose = () => {
    setAssigneeType(initialAssigneeType ?? ASSIGN_TO_USER);
    setSelectedUser('');
    setSelectedOrg('');
    debouncedSetOrgSearchQuery.cancel();
    setDebouncedOrgSearchQuery('');
    onClose();
  };

  const resetOrgSearch = () => {
    debouncedSetOrgSearchQuery.cancel();
    setDebouncedOrgSearchQuery('');
  };

  const handleOrgInputChange = (value) => {
    const nextValue = value ?? '';
    if (nextValue === '') {
      debouncedSetOrgSearchQuery.cancel();
      setDebouncedOrgSearchQuery('');
    } else {
      debouncedSetOrgSearchQuery(nextValue);
    }
    setSelectedOrg('');
  };

  const handleOrgSelectionChange = (value) => {
    const selectedOption = orgOptions.find((option) => option.value === value) ??
      orgOptions.find((option) => option.value === value?.value);

    debouncedSetOrgSearchQuery.cancel();
    setSelectedOrg(selectedOption?.value ?? '');
  };

  const handleOrgLoadOptions = (search) => {
    handleOrgInputChange(search);
  };

  const handleOrgHideDropdown = () => {
    if (!selectedOrg) {
      resetOrgSearch();
    }
  };

  const isValid = () => (
    assigneeType === ASSIGN_TO_USER ? !!selectedUser : !!selectedOrg
  );
  const isAssigneeOptionsLoading = assigneeType === ASSIGN_TO_USER ? loadingEmployees : false;

  const handleSubmit = async () => {
    if (!isValid()) return;

    const taskIds = selectedTasks.map((t) => t.id).join(',');
    const assigneeProviderId = assigneeType === ASSIGN_TO_USER ? currentProviderId : selectedOrg;
    const assigneeEmployeeId = assigneeType === ASSIGN_TO_USER ? selectedUser : undefined;

    try {
      await batchAssignTasks({
        taskIds,
        assigneeProviderId,
        assigneeEmployeeId,
      });

      notifyTaskReassignedSuccess(selectedCount);

      onAfterSubmit({
        assigneeType,
        assigneeId: assigneeEmployeeId || assigneeProviderId,
      });
      handleClose();
    } catch (error) {
      notifyTaskReassignedError();
    }
  };

  const assigneeLabel = assigneeType === ASSIGN_TO_ORG ? 'Organization' : 'User';
  const assignLabel = selectedCount === 1 ?
    `Assign 1 task to ${assigneeLabel}` :
    `Assign ${selectedCount} tasks to ${assigneeLabel}`;

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative"
        style={{ zIndex: 110 }}
        onClose={handleClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-0"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-0"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto" style={{ scrollbarGutter: 'stable' }}>
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-0"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-0"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={'relative transform rounded-lg bg-white text-left shadow-xl ' +
                  'transition-all sm:mb-8 sm:mt-20 sm:w-full sm:max-w-lg'}
                data-testid="reassign-tasks-modal"
              >
                {/* Header */}
                <Dialog.Title
                  as="div"
                  className="bg-white px-6 py-4 flex justify-between rounded"
                >
                  <h3 className="text-lg font-extrabold leading-6 text-dark-blue">
                    Assign tasks
                  </h3>
                  <Icon
                    icon="Cross"
                    size={12}
                    color="dark-blue"
                    onClick={handleClose}
                    className="stroke-current stroke-1 cursor-pointer"
                  />
                </Dialog.Title>

                {/* Content */}
                <Dialog.Description
                  as="div"
                  className="px-6 py-4 bg-light-border-grey border-t border-b border-solid border-med-pale-blue"
                >
                  {isAssignToOrgDisabled && (
                    <div
                      className={[
                        'mb-4 rounded border border-solid border-medium-border-blue',
                        'bg-white px-3 py-2 text-sm text-gray-700',
                      ].join(' ')}
                    >
                      {orgDisabledMessage}
                    </div>
                  )}

                  <RadioField
                    id="assign_to_user_or_org"
                    label="Assign to"
                    labelClassName="mb-2 block font-semibold text-text-blue"
                    required
                    inline
                    className="mb-4"
                    options={assignToOptions}
                    value={assigneeType}
                    onChange={(e) => {
                      const value = e?.target?.value ?? e;
                      setAssigneeType(value);
                      setSelectedUser('');
                      setSelectedOrg('');
                      debouncedSetOrgSearchQuery.cancel();
                      setDebouncedOrgSearchQuery('');
                    }}
                  />

                  {isAssigneeOptionsLoading ? (
                    <div className="flex items-center justify-center py-6" role="alert" aria-live="polite">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {/* User select */}
                      {assigneeType === ASSIGN_TO_USER && (
                        <SelectField
                          id="reassign_to_user"
                          label="Select a User"
                          labelClassName="capitalize"
                          dataTestElement="dropdown_reassign_to_user"
                          shouldSort={false}
                          placeholder="Search"
                          options={employeeOptions}
                          onChange={(v) => setSelectedUser(v)}
                          loadOnMount
                          shouldSearchOnHide
                          value={selectedUser}
                        />
                      )}

                      {/* Org select */}
                      {assigneeType === ASSIGN_TO_ORG && (
                        <>
                          <SelectField
                            id="reassign_to_org"
                            label="Select an Organization"
                            labelClassName="capitalize"
                            dataTestElement="dropdown_reassign_to_org"
                            shouldSort={false}
                            placeholder="Search"
                            options={orgOptions}
                            onChange={handleOrgSelectionChange}
                            onHideDropdown={handleOrgHideDropdown}
                            loadOptions={handleOrgLoadOptions}
                            searchPlaceholderValue="Search..."
                            value={selectedOrg}
                          />
                          {loadingOrgs && (
                            <div className="mt-1 text-xs text-medium-grey" role="status" aria-live="polite">
                              Loading...
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </Dialog.Description>

                {/* Footer */}
                <div className="gap-2 bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                  <Button
                    label={assignLabel}
                    data-testid="confirm-reassign-tasks"
                    primary
                    disabled={!isValid() || isAssigning}
                    onClick={handleSubmit}
                    className="font-regular-font"
                  />
                  <Button
                    label="Cancel"
                    data-testid="cancel-reassign-tasks"
                    secondary
                    onClick={handleClose}
                    className="font-regular-font"
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

ReassignTasksModalContent.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAfterSubmit: PropTypes.func.isRequired,
  selectedTasks: PropTypes.arrayOf(PropTypes.object),
  currentProviderId: PropTypes.string.isRequired,
  leadSCNFlag: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  initialAssigneeType: PropTypes.oneOf([ASSIGN_TO_USER, ASSIGN_TO_ORG]),
};

ReassignTasksModalContent.defaultProps = {
  selectedTasks: [],
  leadSCNFlag: null,
  initialAssigneeType: ASSIGN_TO_USER,
};

// Wrapper: only mounts the modal content (and fires API calls) once `show` becomes true
const ReassignTasksModal = (props) => {
  if (!props.show) return null; // eslint-disable-line react/prop-types
  return <ReassignTasksModalContent {...props} />;
};

function mapStateToProps(state) {
  return {
    leadSCNFlag: wrk31LeadScnProviders(state),
  };
}

export default connect(mapStateToProps)(ReassignTasksModal);
